import useSWR from 'swr';
import { isAxiosError } from 'axios';
import { getApprovalTypeGuideUrl } from '@/constant/approval';
import type { ApprovalTypeGuide } from '@/types/approval';
import { getNestedDataFetcher } from '@/utils/apis';

/** 가이드 미등록 응답(404/0404)은 정상 케이스라 에러로 올리지 않음 */
const isNotFoundError = (err: unknown): boolean => {
  if (!isAxiosError(err)) return false;
  if (err.response?.status === 404) return true;
  const code = (
    err.response?.data as { message?: { code?: string } } | undefined
  )?.message?.code;
  return code === '0404';
};

/**
 * 양식별 작성 가이드 단건 조회. code/companyCode 중 하나라도 없으면 호출 skip (SWR key null).
 * 미등록 양식은 200 + `guide: null`, 없는 code 는 404/0404 — 둘 다 가이드 없음으로 동일 처리.
 */
const useApprovalTypeGuide = (
  code?: number | null,
  companyCode?: string | null,
) => {
  // router.query 미해석 구간의 NaN 이 그대로 URL 에 박히지 않게 isFinite 로 게이팅
  const key =
    Number.isFinite(code) && companyCode
      ? getApprovalTypeGuideUrl(code as number, companyCode)
      : null;

  const { data, error, isLoading } = useSWR<ApprovalTypeGuide | null>(
    key,
    getNestedDataFetcher,
    {
      shouldRetryOnError: (err) => !isNotFoundError(err),
    },
  );

  const guide = data?.guide?.trim() ? data.guide : null;

  return {
    guide,
    source: data?.source ?? null,
    isLoading,
    error: error && !isNotFoundError(error) ? error : undefined,
  };
};

export default useApprovalTypeGuide;
